// src/pages/ServerDatabasePage.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageTransition from '../components/Shared/PageTransition';
import Pagination from '../components/Shared/Pagination';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

// ServerDatabase Components (Apple / Grok Aesthetic)
import ServerHero from '../components/ServerDatabase/ServerHero';
import ServerKpiStats from '../components/ServerDatabase/ServerKpiStats';
import ServerSearchCapsule from '../components/ServerDatabase/ServerSearchCapsule';
import ServerFilterToolbar from '../components/ServerDatabase/ServerFilterToolbar';
import ServerGrid from '../components/ServerDatabase/ServerGrid';
import ServerTable from '../components/ServerDatabase/ServerTable';
import ServerDetailsModal from '../components/ServerDatabase/ServerDetailsModal';
import { useNotification } from '../contexts/NotificationContext';

import {
  DEFAULT_SERVER_DATABASE,
  normalizeServer
} from '../components/ServerDatabase/serverDatabaseData';

import {
  WS_SERVER_DB_TYPES,
  WS_SERVER_DB_TOPICS,
  getDbAppEndpoint
} from '../config/wsServerDbConfig';

import {
  WS_DIRECT_RCON_TYPES,
  WS_DIRECT_RCON_TOPICS,
  getDirectRconAppEndpoint,
  getDirectRconUserQueueTopic
} from '../config/wsDirectRconConfig';

import './ServerDatabasePage.css';

const createConsoleLine = (type, text) => ({
  type, // 'system', 'command', 'response', 'error', 'success', 'warning', 'info'
  text,
  timestamp: new Date().toLocaleTimeString()
});

const SERVERS_PER_PAGE = 12;

const ServerDatabasePage = ({ openModal, closeModal }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  // --- Auth & WebSocket ---
  const { isAuthenticated } = useAuth();
  const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();

  // --- Search & Filter State ---
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filterVersion, setFilterVersion] = useState(searchParams.get('version') || '');
  const [filterPlayers, setFilterPlayers] = useState(parseInt(searchParams.get('players'), 10) || 0);
  const [filterTime, setFilterTime] = useState(searchParams.get('time') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'online-desc');
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'grid');

  // --- Server Data State ---
  const [servers, setServers] = useState(DEFAULT_SERVER_DATABASE);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(DEFAULT_SERVER_DATABASE.length);

  // --- Modals & Toasts ---
  const [selectedModalServer, setSelectedModalServer] = useState(null);
  const { showToast } = useNotification();

  // Copy IP handler
  const handleCopyIp = useCallback((ipPort) => {
    if (!ipPort) return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(ipPort).then(() => {
        showToast(isEn ? `IP address copied: ${ipPort}` : `IP адрес скопирован: ${ipPort}`);
      });
    } else {
      const temp = document.createElement('textarea');
      temp.value = ipPort;
      document.body.appendChild(temp);
      temp.select();
      document.execCommand('copy');
      document.body.removeChild(temp);
      showToast(isEn ? `IP address copied: ${ipPort}` : `IP адрес скопирован: ${ipPort}`);
    }
  }, [showToast, isEn]);

  // Reset all filters
  const handleResetAll = useCallback(() => {
    setSearchQuery('');
    setFilterVersion('');
    setFilterPlayers(0);
    setFilterTime('all');
    setSortBy('online-desc');
    setSearchParams({}, { replace: true });
    showToast(isEn ? 'All filters reset to default' : 'Все фильтры сброшены к начальным');
  }, [setSearchParams, showToast, isEn]);

  const serverDbSubscriptions = useRef({});
  const stopDirectRconSessionRef = useRef();

  // --- WebSocket Handlers for Server DB ---
  const handleServerList = useCallback((data) => {
    console.log('Received server list data from backend:', data);
    if (data?.content && Array.isArray(data.content) && data.content.length > 0) {
      const normalized = data.content.map(normalizeServer);
      setServers(normalized);
      setTotalPages(data.totalPages || 1);
      setTotalElements(data.totalElements || normalized.length);
    } else {
      // Fallback to default catalog if database has no rows
      setServers(DEFAULT_SERVER_DATABASE);
      setTotalElements(DEFAULT_SERVER_DATABASE.length);
      setTotalPages(1);
    }
    setLoading(false);
  }, []);

  const handleFetchError = useCallback((errorData) => {
    const errorMessage = errorData?.message || errorData?.error || 'Failed to fetch servers.';
    console.warn('Server Fetch Error from backend:', errorMessage);
    // Graceful fallback to default servers so the page is always functional
    setServers(DEFAULT_SERVER_DATABASE);
    setTotalElements(DEFAULT_SERVER_DATABASE.length);
    setTotalPages(1);
    setLoading(false);
  }, []);

  // WebSocket Subscription Setup
  useEffect(() => {
    if (!isWebSocketConnected || !isAuthenticated) {
      Object.values(serverDbSubscriptions.current).forEach(unsub => unsub?.());
      serverDbSubscriptions.current = {};
      return;
    }

    Object.values(serverDbSubscriptions.current).forEach(unsub => unsub?.());
    serverDbSubscriptions.current.list = subscribe(
      `${WS_SERVER_DB_TOPICS.USER_QUEUE_BASE}${WS_SERVER_DB_TOPICS.USER_LIST_DESTINATION}`,
      (data) => {
        if (data?.type === WS_SERVER_DB_TYPES.TYPE_SERVER_LIST) {
          handleServerList(data.data || data);
        } else {
          handleServerList(data);
        }
      }
    );
    serverDbSubscriptions.current.error = subscribe(
      `${WS_SERVER_DB_TOPICS.USER_QUEUE_BASE}${WS_SERVER_DB_TOPICS.USER_ERROR_DESTINATION}`,
      (errorData) => {
        const errMsg = errorData?.message || (typeof errorData === 'string' ? errorData : '');
        if (
          (errMsg && (errMsg.toLowerCase().includes('permission denied') || errMsg.toLowerCase().includes('professional plan'))) ||
          errorData?.requiredPlan ||
          errorData?.permission === 'module.directrcon.connect'
        ) {
          stopDirectRconSessionRef.current?.(false);
          openModal('planError', {
            requiredPlan: errorData?.requiredPlan || 'Professional',
            permission: errorData?.permission || 'module.directrcon.connect'
          });
          return;
        }
        if (errorData?.type === WS_SERVER_DB_TYPES.TYPE_FETCH_ERROR) {
          handleFetchError(errorData.data || errorData);
        } else {
          handleFetchError(errorData);
        }
      }
    );

    return () => {
      Object.values(serverDbSubscriptions.current).forEach(unsub => unsub?.());
      serverDbSubscriptions.current = {};
    };
  }, [isWebSocketConnected, isAuthenticated, subscribe, handleServerList, handleFetchError, openModal]);

  // Trigger server fetch from backend
  const triggerServerFetch = useCallback((page = 1) => {
    if (!isWebSocketConnected || !isAuthenticated) return;
    setLoading(true);

    const payload = {
      page: page,
      size: SERVERS_PER_PAGE,
      status: null,
      search: searchQuery || null,
      version: filterVersion || null,
      players: filterPlayers > 0 ? String(filterPlayers) : null,
      tested: filterTime !== 'all' ? filterTime : null
    };
    sendMessage(getDbAppEndpoint(WS_SERVER_DB_TOPICS.GET_SERVERS), payload);
  }, [isWebSocketConnected, isAuthenticated, sendMessage, searchQuery, filterVersion, filterPlayers, filterTime]);

  useEffect(() => {
    if (isWebSocketConnected && isAuthenticated) {
      triggerServerFetch(1);
    }
  }, [isWebSocketConnected, isAuthenticated, triggerServerFetch]);

  // --- Direct RCON Session State & Handlers ---
  const [directRconSession, setDirectRconSession] = useState({
    isSessionActive: false,
    isConnected: false,
    targetIpPort: null,
    rconHost: null,
    rconPort: null,
    consoleLines: [],
    isConnecting: false,
    statusText: 'Idle',
    statusColor: 'text-gray-400',
    sessionError: null,
  });
  const directRconSessionRef = useRef(directRconSession);
  useEffect(() => {
    directRconSessionRef.current = directRconSession;
  }, [directRconSession]);

  const directRconSubscriptions = useRef({});

  // Cleanup backend session on unmount
  useEffect(() => {
    return () => {
      if (directRconSessionRef.current.targetIpPort && isWebSocketConnected) {
        sendMessage(getDirectRconAppEndpoint(WS_DIRECT_RCON_TOPICS.APP_REQUEST_DISCONNECT), {});
      }
    };
  }, [isWebSocketConnected, sendMessage]);

  // Disconnect backend session if user closes window/tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (directRconSessionRef.current.targetIpPort && isWebSocketConnected) {
        sendMessage(getDirectRconAppEndpoint(WS_DIRECT_RCON_TOPICS.APP_REQUEST_DISCONNECT), {});
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isWebSocketConnected, sendMessage]);

  const handleDirectRconStatus = useCallback((message) => {
    const currentSessionState = directRconSessionRef.current;
    if (!message || (!currentSessionState.targetIpPort && message.type !== WS_DIRECT_RCON_TYPES.CONNECTED)) {
      return;
    }

    const { type, data } = message;
    const messageText = data?.message || (typeof data === 'string' ? data : type);
    const rconHost = data?.ip || currentSessionState.rconHost;
    const rconPort = data?.rconPort || currentSessionState.rconPort;

    setDirectRconSession(prev => {
      let newLines = [...prev.consoleLines];
      let newStatusText = prev.statusText;
      let newStatusColor = prev.statusColor;
      let newIsConnecting = prev.isConnecting;
      let newIsConnected = prev.isConnected;
      let newSessionError = prev.sessionError;
      let newIsSessionActive = prev.isSessionActive;

      switch (type) {
        case WS_DIRECT_RCON_TYPES.STATUS_UPDATE:
          if (messageText) newLines.push(createConsoleLine('system', `[SYS] ${messageText}`));
          newStatusText = messageText || (isEn ? "Processing..." : "Обработка...");
          newStatusColor = "text-gray-400";
          newIsSessionActive = true;
          break;
        case WS_DIRECT_RCON_TYPES.CONNECTED:
          newLines.push(createConsoleLine('success', `[OK] ${isEn ? 'Connected to RCON' : 'Успешное подключение к RCON'} ${rconHost}:${rconPort}! ${isEn ? 'You can now enter commands.' : 'Теперь можно вводить команды.'}`));
          newStatusText = `RCON: ${rconHost}:${rconPort}`;
          newStatusColor = "text-emerald-400";
          newIsConnecting = false;
          newIsConnected = true;
          newSessionError = null;
          newIsSessionActive = true;
          break;
        case WS_DIRECT_RCON_TYPES.AUTH_FAILED:
          newLines.push(createConsoleLine('error', `[ERR] ${isEn ? 'RCON Authentication Failed:' : 'Ошибка авторизации RCON:'} ${messageText || (isEn ? "Invalid credentials." : "Неверный пароль.")}`));
          newStatusText = isEn ? "Auth Failed" : "Ошибка авторизации";
          newStatusColor = "text-red-400";
          newIsConnecting = false;
          newIsConnected = false;
          newSessionError = messageText || (isEn ? "RCON authentication failed with the saved credentials." : "Неверный пароль RCON в базе данных.");
          newIsSessionActive = false;
          break;
        case WS_DIRECT_RCON_TYPES.CONNECTION_FAILED:
          if (
            data?.requiredPlan ||
            (messageText && (messageText.toLowerCase().includes('permission denied') || messageText.toLowerCase().includes('professional plan'))) ||
            data?.permission === 'module.directrcon.connect'
          ) {
            stopDirectRconSessionRef.current?.(false);
            openModal('planError', {
              requiredPlan: data?.requiredPlan || 'Professional',
              permission: data?.permission || 'module.directrcon.connect'
            });
            return {
              ...prev,
              targetIpPort: null,
              isSessionActive: false,
              isConnected: false,
              isConnecting: false,
            };
          }
          newLines.push(createConsoleLine('error', `[ERR] ${isEn ? 'RCON Connection Failed:' : 'Не удалось подключиться к RCON:'} ${messageText || ""}`));
          newStatusText = isEn ? "Connection Failed" : "Ошибка подключения";
          newStatusColor = "text-red-400";
          newIsConnecting = false;
          newIsConnected = false;
          newSessionError = messageText || (isEn ? "Failed to establish RCON connection." : "Не удалось установить RCON соединение.");
          newIsSessionActive = false;
          break;
        case WS_DIRECT_RCON_TYPES.NO_DETAILS:
          newLines.push(createConsoleLine('error', `[ERR] ${messageText || (isEn ? "RCON details not available in database." : "Данные RCON отсутствуют в базе.")}`));
          newStatusText = isEn ? "RCON Details Missing" : "Нет данных RCON";
          newStatusColor = "text-red-400";
          newIsConnecting = false;
          newIsConnected = false;
          newSessionError = messageText || (isEn ? "RCON port or password not found for this server." : "Порт или пароль RCON для этого сервера не найдены в базе.");
          newIsSessionActive = false;
          break;
        case WS_DIRECT_RCON_TYPES.DISCONNECTED:
          newLines.push(createConsoleLine('system', `[SYS] ${isEn ? 'Disconnected:' : 'Отключено:'} ${messageText || ""}`));
          newStatusText = isEn ? "Disconnected" : "Отключено";
          newStatusColor = "text-gray-400";
          newIsConnected = false;
          newIsSessionActive = false;
          break;
        default:
          console.warn("Unhandled direct RCON status/result type:", type, data);
      }

      if (newLines.length > 200) newLines = newLines.slice(-200);
      return {
        ...prev,
        consoleLines: newLines,
        statusText: newStatusText,
        statusColor: newStatusColor,
        isConnecting: newIsConnecting,
        isConnected: newIsConnected,
        sessionError: newSessionError,
        isSessionActive: newIsSessionActive,
        rconHost: rconHost || prev.rconHost,
        rconPort: rconPort || prev.rconPort,
      };
    });
  }, [isEn, openModal]);

  const handleDirectRconCommandResponse = useCallback((message) => {
    const currentSessionState = directRconSessionRef.current;
    if (!message || !currentSessionState.isSessionActive) return;

    const responseText = message.data?.response || "[Empty response]";
    setDirectRconSession(prev => ({
      ...prev,
      consoleLines: [...prev.consoleLines, createConsoleLine('response', responseText)].slice(-200),
    }));
  }, []);

  // Persistent Direct RCON WebSocket Subscriptions
  useEffect(() => {
    if (!isWebSocketConnected || !isAuthenticated) {
      Object.values(directRconSubscriptions.current).forEach(unsub => unsub?.());
      directRconSubscriptions.current = {};
      return;
    }

    Object.values(directRconSubscriptions.current).forEach(unsub => unsub?.());
    directRconSubscriptions.current.status = subscribe(
      getDirectRconUserQueueTopic(WS_DIRECT_RCON_TOPICS.USER_STATUS_DESTINATION),
      handleDirectRconStatus
    );
    directRconSubscriptions.current.response = subscribe(
      getDirectRconUserQueueTopic(WS_DIRECT_RCON_TOPICS.USER_RESPONSE_DESTINATION),
      handleDirectRconCommandResponse
    );

    return () => {
      Object.values(directRconSubscriptions.current).forEach(unsub => unsub?.());
      directRconSubscriptions.current = {};
    };
  }, [isWebSocketConnected, isAuthenticated, subscribe, handleDirectRconStatus, handleDirectRconCommandResponse]);

  const isStoppingSessionRef = useRef(false);
  const stopDirectRconSession = useCallback((notifyBackend = true) => {
    if (isStoppingSessionRef.current) return;
    if (!directRconSessionRef.current.targetIpPort && !directRconSessionRef.current.isSessionActive) return;
    isStoppingSessionRef.current = true;

    if (notifyBackend && isWebSocketConnected) {
      sendMessage(getDirectRconAppEndpoint(WS_DIRECT_RCON_TOPICS.APP_REQUEST_DISCONNECT), {});
    }

    setDirectRconSession({
      isSessionActive: false,
      isConnected: false,
      targetIpPort: null,
      rconHost: null,
      rconPort: null,
      consoleLines: [],
      isConnecting: false,
      statusText: 'Idle',
      statusColor: 'text-gray-400',
      sessionError: null,
    });

    if (closeModal) {
      closeModal();
    } else {
      openModal?.(null);
    }
    isStoppingSessionRef.current = false;
  }, [isWebSocketConnected, sendMessage, closeModal, openModal]);

  stopDirectRconSessionRef.current = stopDirectRconSession;

  const handleDirectRconCommandFromModal = useCallback((command) => {
    if (!directRconSessionRef.current.isConnected || !directRconSessionRef.current.targetIpPort || !isWebSocketConnected) {
      setDirectRconSession(prev => ({
        ...prev,
        consoleLines: [...prev.consoleLines, createConsoleLine('error', isEn ? "Cannot send command: RCON not connected." : "Невозможно отправить команду: RCON не подключен.")]
      }));
      return;
    }
    setDirectRconSession(prev => ({
      ...prev,
      consoleLines: [...prev.consoleLines, createConsoleLine('command', command)]
    }));
    sendMessage(getDirectRconAppEndpoint(WS_DIRECT_RCON_TOPICS.APP_SEND_COMMAND), { command });
  }, [isWebSocketConnected, sendMessage, isEn]);

  const handleStartRconConnect = useCallback((serverIpPort) => {
    if (!serverIpPort) return;

    if (directRconSessionRef.current.isSessionActive) {
      openModal?.('console', {
        title: isEn ? "Session Active" : "Сессия уже активна",
        lines: [
          createConsoleLine('warning', isEn ? `An RCON session for ${directRconSessionRef.current.targetIpPort} is already active.` : `RCON сессия для ${directRconSessionRef.current.targetIpPort} уже активна.`),
          createConsoleLine('info', isEn ? "Please disconnect the current session before starting a new one." : "Пожалуйста, отключите текущую сессию перед началом новой.")
        ],
        isLoading: directRconSessionRef.current.isConnecting,
        status: directRconSessionRef.current.statusText,
        statusColor: directRconSessionRef.current.statusColor,
        maxWidth: 'max-w-md',
        actions: [{ label: isEn ? "Disconnect" : "Отключить", onClick: () => stopDirectRconSession(true), primary: true }]
      });
      return;
    }

    if (!isAuthenticated || !isWebSocketConnected) {
      openModal?.('console', {
        title: `RCON: ${serverIpPort}`,
        lines: [createConsoleLine('error', isEn ? 'Authentication or WebSocket connection required.' : 'Требуется авторизация и подключение к WebSocket.')],
        isLoading: false,
        status: isEn ? "Error" : "Ошибка",
        statusColor: "text-red-400",
        maxWidth: 'max-w-lg',
        actions: [{ label: isEn ? "Close" : "Закрыть", onClick: () => openModal(null) }]
      });
      return;
    }

    const initialLine = createConsoleLine('system', isEn ? `Connecting to RCON ${serverIpPort}...` : `Подключение к RCON ${serverIpPort}...`);

    // Open console modal immediately for instant UI feedback
    openModal?.('console', {
      title: serverIpPort,
      lines: [initialLine],
      isLoading: true,
      status: isEn ? "Connecting..." : "Подключение...",
      statusColor: "text-amber-400",
      error: null,
      actions: [
        {
          label: isEn ? "Disconnect" : "Отключиться",
          onClick: () => stopDirectRconSession(true),
          primary: false,
          icon: "fa-times-circle",
          disabled: false,
        }
      ],
      maxWidth: 'max-w-3xl',
      isConsoleInputEnabled: false,
      onSendCommand: handleDirectRconCommandFromModal,
      onCloseOverride: () => stopDirectRconSession(true)
    });

    setDirectRconSession({
      isSessionActive: true,
      isConnected: false,
      targetIpPort: serverIpPort,
      rconHost: null,
      rconPort: null,
      consoleLines: [initialLine],
      isConnecting: true,
      statusText: isEn ? "Connecting..." : "Подключение...",
      statusColor: "text-amber-400",
      sessionError: null,
    });

    // Send direct connect request to Backend
    sendMessage(getDbAppEndpoint(WS_SERVER_DB_TOPICS.APP_DIRECT_CONNECT), { ip: serverIpPort });

  }, [isAuthenticated, isWebSocketConnected, sendMessage, openModal, stopDirectRconSession, handleDirectRconCommandFromModal, isEn]);

  // Synchronize modal state with direct RCON session
  useEffect(() => {
    if (directRconSession.targetIpPort) {
      openModal?.('console', {
        title: directRconSession.targetIpPort,
        lines: directRconSession.consoleLines,
        isLoading: directRconSession.isConnecting,
        status: directRconSession.statusText,
        statusColor: directRconSession.statusColor,
        error: directRconSession.sessionError,
        actions: [
          {
            label: directRconSession.isConnected || directRconSession.isConnecting
              ? (isEn ? "Disconnect" : "Отключиться")
              : (isEn ? "Close" : "Закрыть"),
            onClick: () => stopDirectRconSession(true),
            primary: directRconSession.isConnected,
            icon: directRconSession.isConnected || directRconSession.isConnecting ? "fa-plug-circle-bolt" : "fa-times-circle",
            disabled: false,
          }
        ],
        maxWidth: 'max-w-3xl',
        isConsoleInputEnabled: directRconSession.isConnected && !directRconSession.isConnecting,
        onSendCommand: handleDirectRconCommandFromModal,
        onCloseOverride: () => stopDirectRconSession(true)
      });
    }
  }, [directRconSession, isEn, openModal, stopDirectRconSession, handleDirectRconCommandFromModal]);

  // --- Client-side Reactive Filtering & Sorting ---
  const filteredServers = useMemo(() => {
    let result = [...servers];

    // 1. Поиск по строке (name, ip, core, version, vulnDetail)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(item => {
        return (
          item.name?.toLowerCase().includes(q) ||
          item.ip?.toLowerCase().includes(q) ||
          item.core?.toLowerCase().includes(q) ||
          item.version?.toLowerCase().includes(q) ||
          item.vulnDetail?.toLowerCase().includes(q)
        );
      });
    }

    // 2. Фильтр: Версия (ручной ввод пользователем)
    if (filterVersion.trim()) {
      const v = filterVersion.toLowerCase().trim();
      result = result.filter(item => {
        return (
          item.version?.toLowerCase().includes(v) ||
          item.versionTag?.toLowerCase().includes(v)
        );
      });
    }

    // 3. Фильтр: Количество игроков (порог от)
    if (filterPlayers > 0) {
      result = result.filter(item => item.players >= filterPlayers);
    }

    // 4. Фильтр: Время тестирования
    if (filterTime !== 'all') {
      result = result.filter(item => {
        if (filterTime === '24h') return (item.testedHoursAgo || 1) <= 24;
        if (filterTime === '7d') return (item.testedHoursAgo || 1) <= 168;
        if (filterTime === '30d') return (item.testedHoursAgo || 1) <= 720;
        return true;
      });
    }

    // 5. Сортировка
    result.sort((a, b) => {
      switch (sortBy) {
        case 'online-desc':
          return (b.players || 0) - (a.players || 0);
        case 'online-asc':
          return (a.players || 0) - (b.players || 0);
        case 'recent':
          return (a.testedHoursAgo || 0) - (b.testedHoursAgo || 0);
        case 'name':
          return (a.ip || '').localeCompare(b.ip || '');
        case 'ping':
          return (a.ping || 0) - (b.ping || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [servers, searchQuery, filterVersion, filterPlayers, filterTime, sortBy]);

  // Dynamic sum of players for KPI
  const sumPlayers = useMemo(() => {
    return servers.reduce((acc, s) => acc + (s.players || 0), 0);
  }, [servers]);

  return (
    <PageTransition>
      <div className="server-db-container">
        {/* 1. Hero Block: Bebas Neue крупный заголовок */}
        <ServerHero />

        {/* 2. Global KPI Counters (Apple / Grok Clean Standard) */}
        <ServerKpiStats
          totalServers="4,820+"
          totalPlayers="128,400+"
        />

        {/* 3. Поисковая капсула Apple Style */}
        <ServerSearchCapsule
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onClearSearch={() => setSearchQuery('')}
        />

        {/* 4. Панель фильтров и переключатель вида */}
        <ServerFilterToolbar
          versionInput={filterVersion}
          onVersionChange={setFilterVersion}
          playersInput={filterPlayers}
          onPlayersChange={setFilterPlayers}
          timeFilter={filterTime}
          onTimeFilterChange={setFilterTime}
          onResetAll={handleResetAll}
          sortBy={sortBy}
          onSortChange={setSortBy}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {/* 5. Режим отображения: Сетка или Таблица */}
        {viewMode === 'grid' ? (
          <ServerGrid
            servers={filteredServers}
            onCopyIp={handleCopyIp}
            onOpenModal={setSelectedModalServer}
            onConnectRcon={handleStartRconConnect}
          />
        ) : (
          <ServerTable
            servers={filteredServers}
            onCopyIp={handleCopyIp}
            onOpenModal={setSelectedModalServer}
            onConnectRcon={handleStartRconConnect}
          />
        )}

        {/* 6. Пагинация при наличии нескольких страниц */}
        {!loading && totalPages > 1 && (
          <div style={{ marginTop: 24 }}>
            <Pagination
              currentPage={1}
              totalPages={totalPages}
              onPageChange={(page) => triggerServerFetch(page)}
            />
          </div>
        )}
      </div>

      {/* 7. Модальное окно сведений о сервере */}
      <ServerDetailsModal
        server={selectedModalServer}
        isOpen={!!selectedModalServer}
        onClose={() => setSelectedModalServer(null)}
        onCopyIp={handleCopyIp}
        onConnectRcon={handleStartRconConnect}
      />
    </PageTransition>
  );
};

export default ServerDatabasePage;